/**
 * Träger-Dashboard: Startseite für Träger-User nach dem Login. Oben die Schulen, in denen
 * der User arbeiten darf, mit dem Stand der Module und „Öffnen“, darunter die Kennzahlen.
 */
import { api } from "../api";
import { openCarrierSchool } from "../utils/carrier-view";
import type { CarrierReport } from "../types";
import { showToast } from "../utils/toast";
import { esc, formatNumber, MODULE_LABELS, moduleBadge, schoolBadges } from "./traeger-shared";

let report: CarrierReport | null = null;
let homeSchoolId: number | null = null;
let defaultSchoolId: number | null = null;

const MODULES = ["planung", "schuelerPortal", "stundenplan"] as const;

export async function render(el: HTMLElement) {
  el.innerHTML = `<div class="page-header"><h1>Schulträger</h1></div>
    <div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;
  const [info, data] = await Promise.all([api.carrier.get(), api.carrier.report()]);
  report = data;
  homeSchoolId = info.homeSchoolId;
  defaultSchoolId = info.defaultSchoolId ?? null;

  const founderWarning = info.founderMissing
    ? `<div class="alert alert-error" style="margin-bottom:1rem">Der Haupt-Trägeradmin ist nicht mehr als Benutzer der Stammschule vorhanden. Bitte wende dich an support@meduvio.de, damit ein neuer Haupt-Trägeradmin eingesetzt wird.</div>`
    : "";
  // Offene Anfragen zum Verbinden, nur beim Haupt-Trägeradmin
  const requestsHint = info.isFounder && info.openRequests > 0
    ? `<div class="alert alert-info" style="margin-bottom:1rem;display:flex;justify-content:space-between;align-items:center;gap:1rem">
        <span>${info.openRequests === 1 ? "1 Schule möchte" : `${info.openRequests} Schulen möchten`} sich mit dem Schulträger verbinden.</span>
        <a href="#/traeger-schulen" style="font-weight:600;white-space:nowrap">Zu den Anfragen</a>
      </div>`
    : "";

  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>${esc(info.name)}</h1>
        <div class="text-muted" style="font-size:.875rem;margin-top:.25rem">Schulträger mit ${data.schools.length === 1 ? "1 Schule" : `${data.schools.length} Schulen`} in deinem Zugriff</div>
      </div>
    </div>
    ${founderWarning}
    ${requestsHint}
    <div class="card table-wrap" id="tr-schools"></div>
    <p class="text-muted" style="font-size:.8125rem;margin:.5rem 0 0">★ Standard-Schule: Ein neuer Tab und die Anmeldung öffnen direkt diese Schule. Ohne Standard-Schule startest du im Träger-Bereich.</p>
    <h2 class="carrier-section-title">Kennzahlen</h2>
    <div class="card table-wrap" id="tr-counts"></div>`;

  renderSchools(el);
  renderCounts(el);

  el.querySelector("#tr-schools")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;
    const btn = target.closest<HTMLButtonElement>("[data-open]");
    if (btn) { openCarrierSchool(Number(btn.dataset.open), homeSchoolId); return; }
    const star = target.closest<HTMLButtonElement>("[data-default]");
    if (!star) return;
    // Es gibt höchstens eine Standard-Schule; erneuter Klick auf den Stern entfernt sie
    const id = Number(star.dataset.default);
    const next = defaultSchoolId === id ? null : id;
    try {
      await api.carrier.setDefaultSchool(next);
      defaultSchoolId = next;
      renderSchools(el);
      showToast(next ? "Standard-Schule gesetzt" : "Standard-Schule entfernt");
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
}

function renderSchools(el: HTMLElement) {
  const rows = report!.schools;
  el.querySelector("#tr-schools")!.innerHTML = `
    <table>
      <thead><tr>
        <th style="width:44px"></th>
        <th>Schule</th>
        ${MODULES.map(m => `<th>${esc(MODULE_LABELS[m])}</th>`).join("")}
        <th style="width:100px"></th>
      </tr></thead>
      <tbody>
        ${rows.length === 0 ? `<tr><td colspan="${MODULES.length + 3}" class="empty-state">Keine Schulen</td></tr>` : rows.map(s => `<tr>
          <td>${starButton(s.id)}</td>
          <td>
            <div style="font-weight:500">${esc(s.name)} ${schoolBadges(s)}</div>
            ${s.ort ? `<div class="text-muted" style="font-size:.8125rem">${esc(s.ort)}</div>` : ""}
          </td>
          ${MODULES.map(m => `<td>${moduleBadge(s.modules[m])}</td>`).join("")}
          <td style="text-align:right"><button class="btn btn-primary btn-sm" data-open="${s.id}">Öffnen</button></td>
        </tr>`).join("")}
      </tbody>
    </table>`;
}

function starButton(schoolId: number): string {
  const active = schoolId === defaultSchoolId;
  return `<button class="carrier-star${active ? " carrier-star--active" : ""}" data-default="${schoolId}"
    title="${active ? "Standard-Schule. Klicken zum Entfernen" : "Als Standard-Schule festlegen"}"
    aria-label="${active ? "Standard-Schule entfernen" : "Als Standard-Schule festlegen"}" aria-pressed="${active}">${active ? "★" : "☆"}</button>`;
}

function renderCounts(el: HTMLElement) {
  const rows = report!.schools;
  const sum = (key: "classes" | "students" | "practices" | "assignments") => rows.reduce((a, s) => a + s[key], 0);
  const userSum = rows.every(s => s.users != null) ? rows.reduce((a, s) => a + (s.users ?? 0), 0) : null;
  el.querySelector("#tr-counts")!.innerHTML = `
    <table>
      <thead><tr><th>Schule</th><th>Klassen</th><th>Schüler</th><th>Praxisstellen</th><th>Einsätze</th><th>Benutzer</th></tr></thead>
      <tbody>
        ${rows.map(s => `<tr>
          <td style="font-weight:500">${esc(s.name)}</td>
          <td>${formatNumber(s.classes)}</td><td>${formatNumber(s.students)}</td>
          <td>${formatNumber(s.practices)}</td><td>${formatNumber(s.assignments)}</td>
          <td>${formatNumber(s.users)}</td>
        </tr>`).join("")}
        ${rows.length > 1 ? `<tr class="carrier-sum"><td>Summe</td>
          <td>${formatNumber(sum("classes"))}</td><td>${formatNumber(sum("students"))}</td>
          <td>${formatNumber(sum("practices"))}</td><td>${formatNumber(sum("assignments"))}</td>
          <td>${formatNumber(userSum)}</td></tr>` : ""}
      </tbody>
    </table>`;
}
